/**
 * The trim on the roofs the rules stand fighters on (map.ts `authoredRoofAt`): the ridge cap, the
 * hip caps and the ornaments at the ridge's ends, as the 3D kit draws them (render3d/scenery:
 * `hipRoof`'s `ridgeH`/`ridgeW`/`hipH`/`hipW`/`ends`, per building type and landmark). Feet on a
 * ridge stand on its cap, not 30 cm inside it.
 *
 * Kept in step with the drawings by the surfaces checker (`npm run check:surfaces`): change a roof's
 * trim in render3d and the row here with it.
 */
import type { BuildingType } from '../map';

export interface RoofTrim {
  /** The ridge cap: its height over the ridge and its width (metres). */
  readonly ridge: readonly [h: number, w: number];
  /** The hip caps: height over the slope's hip line and width. */
  readonly hip: readonly [h: number, w: number];
  /** What ends the ridge (or tops a pyramid). */
  readonly ends: 'curl' | 'chiwen' | 'finial' | 'none';
  /** The ridge's inset from each end, when not the house roof's (`ROOF.house.inset`). */
  readonly inset?: number;
  /**
   * A hip-and-gable roof (歇山): the end slopes stop at this share of the rise, where a gable
   * (山花) stands upright up to the ridge, which then runs out to it. The gable stands where the end
   * slope reaches that share (`-overhang + gable · (inset + overhang)` in from the footprint's end);
   * a verge cap (垂脊, the hip's size) runs down its edge to the hip, which goes on to the corner.
   */
  readonly gable?: number;
}

/**
 * The hip-and-gable's numbers (歇山, docs/archive/SCENERY_AUDIT.md lane L2): the ridge 1.85 m in from each
 * end and the gable at 45 % of the rise, so the gable stands about over the end wall (0.5 m in).
 */
const GABLE = { inset: 1.85, gable: 0.45 } as const;
/** A tiled house's (house.ts `houseOpts`): also a plain inn. */
const HOUSE: RoofTrim = { ridge: [0.32, 0.3], hip: [0.16, 0.2], ends: 'curl', ...GABLE };
/** The hipped house roof (the guard post, the stable, the granary). */
const HOUSE_HIP: RoofTrim = { ridge: [0.32, 0.3], hip: [0.16, 0.2], ends: 'curl' };
const THATCH: RoofTrim = { ridge: [0.22, 0.42], hip: [0.16, 0.2], ends: 'none' };
/** The landmark houses' lower ridges (elder's house, inn, shop: landmarks/*). */
const LANDMARK_HOUSE: RoofTrim = { ridge: [0.15, 0.3], hip: [0.12, 0.2], ends: 'curl', ...GABLE };

/** By landmark id first (landmarks registered by id), then by building type. */
const BY_ID: Readonly<Record<string, RoofTrim>> = {
  'yanyu-elder-house': LANDMARK_HOUSE,
  'yanyu-inn': LANDMARK_HOUSE,
  'yanyu-shop': LANDMARK_HOUSE,
  'yanyu-shrine': { ridge: [0.14, 0.38], hip: [0.05, 0.24], ends: 'chiwen', ...GABLE },
};

const BY_TYPE: Partial<Readonly<Record<BuildingType, RoofTrim>>> = {
  house: HOUSE,
  guard: HOUSE_HIP,
  stable: HOUSE_HIP,
  granary: HOUSE_HIP,
  inn: HOUSE,
  hall: { ridge: [0.55, 0.36], hip: [0.2, 0.24], ends: 'chiwen', ...GABLE },
  recruit: { ridge: [0.14, 0.38], hip: [0.05, 0.24], ends: 'chiwen', ...GABLE },
  pavilion: { ridge: [0.14, 0.24], hip: [0.05, 0.22], ends: 'finial' },
  kiosk: { ridge: [0.2, 0.2], hip: [0.12, 0.16], ends: 'finial' },
  shed: { ridge: [0.22, 0.4], hip: [0.14, 0.24], ends: 'none' },
  stall: { ridge: [0.05, 0.12], hip: [0.04, 0.1], ends: 'none' },
  gate: { ridge: [0.26, 0.3], hip: [0.16, 0.22], ends: 'curl', inset: 0.8 },
  drumtower: { ridge: [0.5, 0.34], hip: [0.2, 0.24], ends: 'chiwen' },
};

/**
 * A gable roof (悬山, lane KIT-EXT): no end slopes at all. The hip-and-gable's rule with the gable at
 * none of the rise (`gable: 0`, map.ts `gableAt` → −overhang): the long slopes run out over the ends'
 * overhang, the gables stand upright at the verge, and the ridge runs out to them. Opt in with the
 * layout's `roof: 'gable'`; the house's trim otherwise (thatch keeps its plain ridge).
 */
function gableOf(t: RoofTrim): RoofTrim {
  return { ...t, gable: 0 };
}

/** The red gate (its `variant`'s colour, layout.ts `COLORS`: blue, red, white, green) ends in dragon-fish. */
const RED_GATE: RoofTrim = { ...BY_TYPE.gate!, ends: 'chiwen' };

/** A roof's trim: by its landmark id, its type, its roof (thatch), else a tiled house's. */
export function roofTrimOf(st: {
  readonly id?: string;
  readonly type?: BuildingType;
  readonly kind: string;
  readonly roof?: string;
  readonly variant?: number;
}): RoofTrim {
  if (st.roof === 'gable') return gableOf(roofTrimOf({ ...st, roof: 'hip' }));
  const byId = st.id ? BY_ID[st.id] : undefined;
  if (byId) return byId;
  const t = st.type ?? (st.kind as BuildingType);
  if (t === 'gate' && (st.variant ?? 0) % 4 === 1) return RED_GATE;
  if (
    st.roof === 'thatch' &&
    (t === 'house' || t === 'granary' || t === 'stable' || t === 'guard' || t === 'inn')
  )
    return THATCH;
  return BY_TYPE[t] ?? HOUSE;
}

/**
 * The top of the ornament at one end of a ridge over a point, as a height over the ridge cap's top,
 * or null off it: `du` along the ridge from the cap's end (frame units), `dv` across from the ridge
 * line, `d` the end's outward direction (−1 at the start, 1 at the end). Mirrors render3d/scenery/
 * parts/roof.ts `ridgeEnds` box for box (the dragon-fish's boxes are not mirrored end to end).
 */
export function ridgeEndTop(t: RoofTrim, du: number, dv: number, d: number): number | null {
  const [rh, w] = t.ridge;
  let top: number | null = null;
  const box = (u0: number, u1: number, hw: number, h: number) => {
    if (du >= Math.min(u0, u1) && du <= Math.max(u0, u1) && Math.abs(dv) <= hw)
      top = Math.max(top ?? -Infinity, h);
  };
  if (t.ends === 'curl') {
    box(-0.18, 0.18, w / 2, 0.22);
    box(d * 0.12, d * 0.42, w * 0.4, 0.38);
    box(d * 0.34, d * 0.56, w * 0.3, 0.52);
  } else if (t.ends === 'chiwen') {
    const sz = 0.28 + rh * 0.6;
    const c = -d * 0.1;
    box(c - sz * 0.5, c + sz * 0.5, w * 0.7, sz * 0.7);
    box(c - sz * 0.45, c + sz * 0.2, w * 0.5, sz * 1.25);
    box(c - sz * 0.75, c - sz * 0.3, w * 0.4, sz * 1.45);
    box(c + sz * 0.2, c + sz * 0.42, w * 0.75, sz * 0.45);
  }
  return top;
}

/** The finial on a pyramid's top over a point `r` from its axis, over the apex, or null off it. */
export function finialTop(r: number): number | null {
  return r <= 0.1 ? 0.9 : r <= 0.14 ? 0.65 : r <= 0.2 ? 0.25 : null;
}

// ── Tiered towers' ledges (lane KIT-EXT) ──────────────────────────────────────────────────────────

/** A structure as the tall buildings' numbers read it (metres; footprint in tiles of 1 m). */
interface TowerSt {
  readonly w: number;
  readonly h: number;
  readonly eave?: number;
  readonly rise?: number;
  readonly roof?: string;
}

/** A ring of eaves round a square storey (render3d/scenery/parts/roof.ts `pentRoof`), from its axis. */
export interface PentRing {
  /** The storey's half-width (its wall line), how far the eave reaches out past it. */
  readonly half: number;
  readonly out: number;
  /** The eave's height at the wall and at its edge mid-side, the corners' lift and its run. */
  readonly hIn: number;
  readonly hOut: number;
  readonly lift: number;
  readonly liftRun: number;
}

/** A square pyramid roof (roof.ts `hipRoof` with `I` = half its side), from its axis. */
export interface PyramidRoof {
  readonly half: number;
  readonly E: number;
  readonly R: number;
  /** Rule overhang, drawn eave reach, the flare's drop and the corners' lift and run. */
  readonly o: number;
  readonly O: number;
  readonly drop: number;
  readonly lift: number;
  readonly liftRun: number;
  /** The hip caps. */
  readonly hipW: number;
  readonly hipH: number;
}

/**
 * The five-storey pagoda as render3d/scenery/landmarks/pagoda.ts draws it (it draws every `pagoda`
 * type), number for number: a 0.6 m platform, storeys 0.2 m narrower each, eaves stepping evenly
 * up to the layout's `eave`, a pent roof (1.35 m out, 0.08 less a storey) round each but the last,
 * the last under a pyramid (rise 2.4 m) and a spire. Change one, change both.
 */
export function pagodaTiers(st: TowerSt): { rings: readonly PentRing[]; top: PyramidRoof; spire: number; h0: number } {
  const n = 5;
  const mid = Math.max(st.w, st.h) / 2;
  const E = st.eave ?? 18;
  const step = (E - 0.6) / n;
  const rings: PentRing[] = [];
  for (let t = 0; t + 1 < n; t++) {
    const eave = 0.6 + step * (t + 1);
    rings.push({ half: mid - 0.25 - t * 0.2, out: 1.35 - t * 0.08, hIn: eave + 0.35, hOut: eave - 0.25, lift: 0.5, liftRun: 1.6 });
  }
  const half = mid - 0.25 - (n - 1) * 0.2;
  const top: PyramidRoof = { half, E, R: 2.4, o: 0.5, O: 1.3, drop: 0.3, lift: 0.55, liftRun: 1.6, hipW: 0.24, hipH: 0.18 };
  return { rings, top, spire: E + 2.4 + 0.8, h0: 0.6 };
}

/** A pent ring's top at a point `r` out from the axis (Chebyshev) and `a` along its side from the side's middle. */
function pentAt(q: PentRing, r: number, a: number): number | null {
  const lam = (r - q.half) / q.out;
  if (lam < 0 || lam > 1) return null;
  // Past the wall's end the corner's fan: the eave's s spread out over the ring's width (pentRoof `height`).
  const e = a - q.half;
  const d = e > 0 && lam > 1e-6 ? q.out - Math.min(q.out, e / lam) : q.out - e;
  const k = Math.max(0, 1 - d / q.liftRun);
  return q.hIn + (q.hOut + q.lift * k * k - q.hIn) * lam;
}

/** A square pyramid's top (`hipRoof`'s plane and flare, its hip caps, its finial), or null past its eave. */
function pyramidAt(p: PyramidRoof, du: number, dv: number): number | null {
  const [x, y] = [Math.abs(du), Math.abs(dv)];
  const r = Math.max(x, y);
  const a = Math.min(x, y);
  if (r > p.half + p.O) return null;
  const top0 = p.E + p.R;
  const t = p.half - r;
  let h: number;
  if (t >= -p.o) h = Math.min(top0, p.E + (p.R * (t + p.o)) / (p.half + p.o));
  else {
    const lam = Math.min(1, (-p.o - t) / (p.O - p.o));
    // The face's s from its corner end (−o … ), spread over the flare as `hipRoof` spreads it.
    const s = p.half - a;
    const ss = s < -p.o && lam > 1e-6 ? -p.o + (s + p.o) / lam : s;
    const dd = Math.max(0, Math.min(p.half * 2 + p.O, ss + p.O));
    const k = Math.max(0, 1 - dd / p.liftRun);
    h = p.E + (p.lift * k * k - p.drop) * lam;
  }
  // The hip caps: apex to the rule corner, on to the lifted tip, along the diagonals.
  const cap = (u0: number, h0: number, u1: number, h1: number) => {
    const along = (x + y) / 2;
    const off = Math.abs(x - y) / Math.SQRT2;
    if (off > p.hipW / 2 || along < u0 || along > u1) return;
    h = Math.max(h, h0 + ((h1 - h0) * (along - u0)) / Math.max(1e-6, u1 - u0) + p.hipH);
  };
  cap(0, top0, p.half + p.o, p.E);
  cap(p.half + p.o, p.E, p.half + p.O, p.E - p.drop + p.lift);
  const f = finialTop(Math.hypot(du, dv));
  if (f !== null) h = Math.max(h, top0 + f);
  return h;
}

/**
 * What a fighter stands on at a point of a pagoda opted into ledges (map.ts `Structure.ledges`),
 * `du`, `dv` metres from its axis (metres over its floor): the top storey's pyramid and spire, else
 * the highest storey's eave reaching out over the point (each eave a ledge a storey below the one
 * over it, as wide as it shows from above: a qinggong climb), else the platform. One height a point:
 * the galleries under the eaves are not stood on.
 */
export function pagodaLedgeAt(st: TowerSt, du: number, dv: number): number {
  const { rings, top, spire, h0 } = pagodaTiers(st);
  const q = Math.hypot(du, dv);
  // The spire (pagoda.ts): a base ring, seven rings narrowing up a mast, a cap.
  if (q <= 0.32) {
    const ring = Math.floor((0.26 - q) / 0.02 + 1e-9);
    return q <= 0.07 ? spire + 3.45 : q <= 0.16 ? spire + 3.0 : ring >= 0 ? spire + 0.55 + 0.32 * Math.min(6, ring) : spire + 0.3;
  }
  const p = pyramidAt(top, du, dv);
  if (p !== null) return p;
  const r = Math.max(Math.abs(du), Math.abs(dv));
  const a = Math.min(Math.abs(du), Math.abs(dv));
  for (let t = rings.length - 1; t >= 0; t--) {
    const h = pentAt(rings[t]!, r, a);
    if (h !== null) return h;
  }
  return h0;
}

/**
 * A watchtower's (render3d/scenery/types/tower.ts `watchtower`) roof opted into ledges: its pyramid
 * over the lookout, else the stone footing's top (a thatched lookout: the ground).
 */
export function watchtowerTopAt(st: TowerSt, du: number, dv: number): number {
  const thatch = st.roof === 'thatch';
  const E = st.eave ?? 6;
  const half = Math.min(st.w, st.h) / 2;
  const d = half - (thatch ? 0.2 : 0.55) + 0.35;
  const p = pyramidAt(
    { half: d, E, R: st.rise ?? 1.5, o: 0.3, O: 0.8, drop: 0.12, lift: thatch ? 0.05 : 0.4, liftRun: Math.min(1.8, d * 1.2), hipW: 0.2, hipH: 0.16 },
    du,
    dv,
  );
  return p ?? (thatch ? 0 : Math.min(2.4, E * 0.25));
}

// ── Generated buildings' tops (lane F5 ROOF-RULES: the c0 world's hub and school buildings) ─────────

/**
 * A generated (layout-less) house's or pavilion's trim: its type's ridge, hips and ends, but a plain
 * hip roof's (no hip-and-gable, the house's own `inset`), as the 3D kit draws one without an `eave`
 * (render3d/scenery/parts/context.ts `ruleRoof`: the gable is for authored roofs only).
 */
export function kindRoofTrim(st: { readonly kind: string; readonly variant?: number }): RoofTrim {
  const t = roofTrimOf({ kind: st.kind, variant: st.variant });
  return { ridge: t.ridge, hip: t.hip, ends: t.ends };
}

/**
 * A generated pagoda's eave (metres): the 3D kit has no pagoda row in its kind roofs and falls back
 * to the house's (render3d/scenery/parts/context.ts `KIND_ROOF`, `ruleRoof`), so the five-storey
 * pagoda (landmarks/pagoda.ts, `pagodaTiers`) is drawn to the house eave. Its ledges are stood on.
 */
export const kindPagodaSt = <S extends TowerSt>(st: S, houseEave: number): S => ({ ...st, eave: houseEave });

/**
 * A generated moon-gate wall's top (render3d/scenery/types/gate.ts `moonGate` with no `passage`:
 * the wall `H` = max(2.4, 1.25 + 1.15 + 0.75) high, under wall.ts `tileCap` either face: tiles from
 * the wall's middle line at H + 0.02 down to the lip, 0.46 m out, at H − 0.29), `dv` metres across
 * from the wall's middle line. Stood on in qinggong: a garden wall's coping.
 */
export function moonWallTopAt(dv: number): number {
  const H = Math.max(2.4, 1.25 + 1.15 + 0.75);
  return H + 0.02 - 0.31 * Math.min(1, Math.abs(dv) / 0.46);
}
